import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { ScreenFields } from '@/components/ScreenForm/ScreenFields'
import { useScreenForm } from '@/components/ScreenForm/useScreenForm'
import { FieldError } from '@/components/ui/field'
import { CacheKeys } from '@/lib/cache'
import {
  type ContractorAnswerSection,
  saveContractorAnswers,
  setConfirmInfoState,
} from '@/models/confirmInfo'
import { fillApplicationErrorMessage } from '@/models/fillApplication'
import type { FillCardForm } from '../App/Customers/FillApplication/useFillCardSave'
import { ReviewSection } from './ReviewSection'

interface ContractorAnswersSectionProps {
  orgId: string
  section: ContractorAnswerSection
  open: boolean
  onOpen: () => void
  onClose: () => void
  /** Told when the dialog gains or loses unsaved edits, so leaving the page can warn. */
  onDirtyChange?: (dirty: boolean) => void
}

/** One screen of a contractor's chat answers: listed on the page, edited in a dialog with the chat's own form. */
export function ContractorAnswersSection({
  orgId,
  section,
  open,
  onOpen,
  onClose,
  onDirtyChange,
}: ContractorAnswersSectionProps) {
  const queryClient = useQueryClient()
  const screenForm = useScreenForm(section.fields)
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // Each opening starts from what's saved. Only the opening: a refetch while open must not undo the edits.
  const wasOpen = useRef(false)
  const { reset } = screenForm
  useEffect(() => {
    if (open && !wasOpen.current) {
      reset()
      setFormError(null)
    }
    wasOpen.current = open
  }, [open, reset])

  const dirty = screenForm.isDirty
  useEffect(() => onDirtyChange?.(dirty), [dirty, onDirtyChange])

  const form: FillCardForm = {
    isDirty: dirty,
    saving,
    discard: () => {
      reset()
      setFormError(null)
    },
    save: async () => {
      setFormError(null)
      if (!screenForm.validate()) return false
      setSaving(true)
      try {
        setConfirmInfoState(
          queryClient,
          orgId,
          await saveContractorAnswers(orgId, section.screen, screenForm.payload())
        )
        // A new legal name renames the account too.
        void queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS, orgId] })
        return true
      } catch (failure) {
        const errors = fieldErrors(failure)
        if (errors) screenForm.setErrors(errors)
        else setFormError(fillApplicationErrorMessage(failure))
        return false
      } finally {
        setSaving(false)
      }
    },
  }

  const summary = (
    <dl className="flex flex-col gap-3">
      {section.rows.map((row) => (
        <div key={row.key} className="flex flex-col gap-0.5">
          <dt className="text-ink-400">{row.question}</dt>
          <dd className="text-foreground wrap-anywhere whitespace-pre-wrap">{row.answer}</dd>
        </div>
      ))}
    </dl>
  )

  return (
    <ReviewSection
      title={section.title}
      checks={[]}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      form={form}
      summary={summary}
    >
      <div className="flex flex-col gap-5">
        <ScreenFields form={screenForm} disabled={saving} />
        <FieldError>{formError}</FieldError>
      </div>
    </ReviewSection>
  )
}

/** The server's refusal per field, `{key: message}` (or a list of them), or null when it isn't one. */
function fieldErrors(failure: unknown): Record<string, string> | null {
  const response = (failure as { response?: { status?: number; data?: unknown } } | null)?.response
  if (response?.status !== 400 || !response.data || typeof response.data !== 'object') return null
  const entries = Object.entries(response.data as Record<string, unknown>).flatMap(([key, messages]) => {
    const message: unknown = Array.isArray(messages) ? messages[0] : messages
    return typeof message === 'string' ? [[key, message]] : []
  })
  return entries.length ? Object.fromEntries(entries) : null
}
